import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Globe, Lock, Sparkles } from 'lucide-react';
import { api } from '../../lib/api';

interface Props {
  /** Se llama tras cambiar los idiomas, para marcar que hay que publicar. */
  onSaved?: () => void;
}

const LOCALE_LABEL: Record<string, string> = { es: 'Español', en: 'Inglés' };

export default function SiteLanguagesTab({ onSaved }: Props) {
  const [loading, setLoading] = useState(true);
  const [multilingualPlan, setMultilingualPlan] = useState(false);
  const [enActive, setEnActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    api.settings
      .capabilities()
      .then((cap) => {
        setMultilingualPlan(cap.multilingual);
        setEnActive(cap.locales.includes('en'));
      })
      .catch(() => setError('No pudimos cargar la configuración de idiomas.'))
      .finally(() => setLoading(false));
  }, []);

  async function toggleEnglish(next: boolean) {
    if (saving) return;
    setSaving(true);
    setError('');
    const prev = enActive;
    setEnActive(next);
    try {
      const locales = next ? ['es', 'en'] : ['es'];
      const res = await api.settings.updateLocales(locales);
      setEnActive(res.locales.includes('en'));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
      onSaved?.();
    } catch (err) {
      setEnActive(prev);
      setError(err instanceof Error ? err.message : 'No pudimos guardar el cambio.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ui-text-muted py-8 justify-center">
        <span className="w-4 h-4 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
        Cargando…
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Español — siempre activo */}
      <div className="flex items-center justify-between gap-4 rounded-lg border border-ui-border bg-ui-surface px-4 py-3.5">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 rounded-full bg-ui-accent/10 text-ui-accent flex items-center justify-center shrink-0">
            <Globe className="w-[1.125rem] h-[1.125rem]" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-ui-text">{LOCALE_LABEL.es}</p>
            <p className="text-sm text-ui-text-subtle">Idioma principal de tu sitio</p>
          </div>
        </div>
        <span className="text-xs font-medium text-ui-success bg-ui-success/10 border border-ui-success/30 rounded-full px-2.5 py-1 shrink-0">
          Siempre activo
        </span>
      </div>

      {/* Inglés — feature de pago */}
      <div className={`rounded-lg border px-4 py-3.5 transition-colors ${enActive ? 'border-ui-accent/40 bg-ui-accent/5' : 'border-ui-border bg-ui-surface'}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${multilingualPlan ? 'bg-ui-accent/10 text-ui-accent' : 'bg-ui-text/10 text-ui-text-subtle'}`}>
              {multilingualPlan ? <Globe className="w-[1.125rem] h-[1.125rem]" /> : <Lock className="w-4 h-4" />}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ui-text">{LOCALE_LABEL.en}</p>
              <p className="text-sm text-ui-text-subtle">
                {multilingualPlan
                  ? 'Cada página podrá tener su versión en inglés, con selector de idioma en el sitio.'
                  : 'Ofrece tu sitio también en inglés con su propio selector de idioma.'}
              </p>
            </div>
          </div>

          {multilingualPlan ? (
            <button
              type="button"
              role="switch"
              aria-checked={enActive}
              disabled={saving}
              onClick={() => toggleEnglish(!enActive)}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-50 ${enActive ? 'bg-ui-accent' : 'bg-ui-border'}`}
            >
              <motion.span
                layout
                transition={{ type: 'spring', stiffness: 500, damping: 32 }}
                className="absolute top-0.5 w-5 h-5 rounded-full bg-ui-bg shadow-sm"
                style={{ left: enActive ? '1.375rem' : '0.125rem' }}
              />
            </button>
          ) : (
            <span className="flex items-center gap-1.5 text-xs font-medium text-ui-accent bg-ui-accent/10 border border-ui-accent/30 rounded-full px-2.5 py-1 shrink-0">
              <Sparkles className="w-3.5 h-3.5" />
              Plan Pro
            </span>
          )}
        </div>

        {/* Upsell cuando el plan no lo incluye */}
        {!multilingualPlan && (
          <div className="mt-3 pt-3 border-t border-ui-border flex items-start gap-2 text-sm text-ui-text-muted">
            <Sparkles className="w-4 h-4 text-ui-accent shrink-0 mt-0.5" />
            <p>
              Tener tu sitio en más de un idioma está disponible en un plan superior.
              Si te interesa, contáctanos para activarlo.
            </p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {justSaved && (
          <motion.p
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-1.5 text-sm text-ui-success"
          >
            <Check className="w-4 h-4" /> Cambios guardados. Publica tu sitio para que se vean.
          </motion.p>
        )}
      </AnimatePresence>

      {error && <p className="text-sm text-ui-danger">{error}</p>}
    </div>
  );
}
